import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  createLibraryRoutine,
  duplicateRoutine,
  getProfilesForRoutine,
  nextLibrarySortIndex,
  removeRoutineFromLibrary,
} from '../features/profiles/routineMutations';
import { bySortIndex } from '../lib/sortIndex';
import type { Profile, Routine } from '../lib/types';
import { uid } from '../lib/uid';
import { useSyncedStore } from '../store/synced';

// Library-scoped tasklist hub per tasks/48. Lists every routine in the
// shared library with the children each is currently linked to, plus
// create / edit / duplicate / delete actions. The per-task "copy to
// another routine" flow stays in RoutineEditorPage (reachable via edit).
export function RoutineLibraryPage() {
  const navigate = useNavigate();
  const tree = useSyncedStore((s) => s.tree);
  const setTree = useSyncedStore((s) => s.setTree);
  const [pendingDelete, setPendingDelete] = useState<Routine | null>(null);

  const routines = (tree?.routines ?? []).slice().sort(bySortIndex);

  const onCreate = () => {
    if (!tree) return;
    const ts = new Date().toISOString();
    const routine: Routine = {
      id: uid(),
      name: '',
      emoji: '✨',
      sortIndex: nextLibrarySortIndex(tree),
      tasks: [],
      createdAt: ts,
      updatedAt: ts,
    };
    setTree(createLibraryRoutine(tree, routine, ts));
    navigate(`/settings/routines/${routine.id}`);
  };

  const onDuplicate = (routineId: string) => {
    if (!tree) return;
    const { tree: next, newRoutineId } = duplicateRoutine(tree, routineId);
    setTree(next);
    navigate(`/settings/routines/${newRoutineId}`);
  };

  const onConfirmDelete = () => {
    if (!tree || !pendingDelete) return;
    setTree(removeRoutineFromLibrary(tree, pendingDelete.id));
    setPendingDelete(null);
  };

  return (
    <main className="settings">
      <Link to="/settings" className="back-button" aria-label="Takaisin asetuksiin">
        <span aria-hidden="true">←</span>
      </Link>
      <h1 className="settings-title">Tehtävälistat</h1>

      <section className="settings-section">
        <h2 className="settings-section-title">Kaikki tehtävälistat</h2>
        {routines.length === 0 ? (
          <p className="settings-hint">Ei tehtävälistoja.</p>
        ) : (
          <ul className="settings-profile-list">
            {routines.map((r) => (
              <RoutineRow
                key={r.id}
                routine={r}
                linkedProfiles={tree ? getProfilesForRoutine(tree, r.id) : []}
                onDuplicate={() => onDuplicate(r.id)}
                onDelete={() => setPendingDelete(r)}
              />
            ))}
          </ul>
        )}
        <button type="button" className="settings-button" onClick={onCreate}>
          + Uusi tehtävälista
        </button>
      </section>

      {pendingDelete && (
        <DeleteConfirmModal
          routine={pendingDelete}
          linkedProfiles={tree ? getProfilesForRoutine(tree, pendingDelete.id) : []}
          onCancel={() => setPendingDelete(null)}
          onConfirm={onConfirmDelete}
        />
      )}
    </main>
  );
}

function RoutineRow({
  routine,
  linkedProfiles,
  onDuplicate,
  onDelete,
}: Readonly<{
  routine: Routine;
  linkedProfiles: Profile[];
  onDuplicate: () => void;
  onDelete: () => void;
}>) {
  const displayName = routine.name || 'Nimetön lista';
  const linkedLabel =
    linkedProfiles.length === 0
      ? 'Ei liitetty kenellekään'
      : `Liitetty: ${linkedProfiles.map((p) => p.name).join(', ')}`;
  return (
    <li className="settings-routine-item">
      <Link
        to={`/settings/routines/${routine.id}`}
        className="settings-profile-row settings-routine-row"
        aria-label={`Muokkaa tehtävälistaa: ${displayName}`}
      >
        <span className="settings-profile-avatar" aria-hidden="true">
          {routine.emoji}
        </span>
        <span className="settings-profile-name">{displayName}</span>
        <span className="settings-profile-edit" aria-hidden="true">
          ✎
        </span>
      </Link>
      <div className="settings-routine-meta">
        {linkedProfiles.length === 0 ? (
          <span className="settings-routine-empty">Ei liitetty kenellekään</span>
        ) : (
          <ul className="settings-routine-chips" aria-label={linkedLabel}>
            {linkedProfiles.map((p) => (
              <li key={p.id} className="settings-routine-chip" title={p.name}>
                <span aria-hidden="true">{p.avatarEmoji}</span>
                <span className="settings-routine-chip-name">{p.name}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="settings-routine-actions">
          <button
            type="button"
            className="settings-button settings-button-small"
            onClick={onDuplicate}
            aria-label={`Duplikoi tehtävälista: ${displayName}`}
          >
            Duplikoi
          </button>
          <button
            type="button"
            className="settings-button settings-button-small settings-button-danger"
            onClick={onDelete}
            aria-label={`Poista tehtävälista: ${displayName}`}
          >
            Poista
          </button>
        </div>
      </div>
    </li>
  );
}

function DeleteConfirmModal({
  routine,
  linkedProfiles,
  onCancel,
  onConfirm,
}: Readonly<{
  routine: Routine;
  linkedProfiles: Profile[];
  onCancel: () => void;
  onConfirm: () => void;
}>) {
  const displayName = routine.name || 'tehtävälista';
  return (
    <dialog open className="confirm-modal" aria-label="Vahvista poisto">
      <button type="button" className="confirm-backdrop" aria-label="Sulje" onClick={onCancel} />
      <div className="confirm-box">
        <h2>Poista tehtävälista?</h2>
        <p>
          {displayName} poistetaan pysyvästi
          {linkedProfiles.length > 0 ? '. Se poistuu myös lapsilta:' : '.'}
        </p>
        {linkedProfiles.length > 0 && (
          <ul className="settings-routine-affected">
            {linkedProfiles.map((p) => (
              <li key={p.id}>
                <span aria-hidden="true">{p.avatarEmoji}</span> {p.name}
              </li>
            ))}
          </ul>
        )}
        <div className="confirm-actions">
          <button type="button" className="settings-button" onClick={onCancel}>
            Peruuta
          </button>
          <button
            type="button"
            className="settings-button settings-button-danger"
            onClick={onConfirm}
          >
            Poista
          </button>
        </div>
      </div>
    </dialog>
  );
}
